import React, { useMemo } from "react";
import {
  Column,
  useTable,
  useFilters,
  useGlobalFilter,
  Row,
  CellProps,
  useSortBy,
  ColumnInstance
} from "react-table";

import { OfficeStatusCell } from "@gettactic/components/src/components/WeeklyOffice/cells/OfficeStatusCell";
import { NameColumnFilter } from "@gettactic/components/src/components/WeeklyOffice/controls/NameColumnFilter";
import { UserCell } from "@gettactic/components/src/components/WeeklyOffice/cells/UserCell";
import { getWorkingLocationStatus } from "@gettactic/components/src/components/WeeklyOffice/cells/WorkingLocationPresenceIcon";
import { SortButton } from "@gettactic/components/src/components/WeeklyOffice/controls/SortButton";
import { makeWeeklyTableId } from "@gettactic/components/src/components/WeeklyOffice/weekly-office-helpers";
import { InfiniteScroll } from "@gettactic/components/src/components/InfiniteScroll/InfiniteScroll";

import {
  PossibleDateSort,
  TableOverviewUser,
  TacticApiClient
} from "@gettactic/api";
import { AuthenticatedUserRow } from "./AuthenticatedUserRow";

import { AuthenticatedUser } from "@gettactic/api";
import { WeeklyTableSkeleton } from "./WeeklyTableSkeleton";
import { useColumnWidth } from "@gettactic/components/src/contexts/ColumnWidthContext";
import { WeeklyDataGridWrapper } from "@gettactic/components/src/components/WeeklyOffice/WeeklyDataLayout";
import { useDashboardFilters } from "@/contexts/DashboardStateContext";
import { ChevronUpIcon } from "lucide-react";
import { cn } from "@gettactic/helpers";
import { TooltipProvider } from "@v2/components/ui/tooltip";
import { useBreakpoint } from "@gettactic/helpers/src/hooks/useBreakpoint";
import { dateStringToLocalTime } from "@gettactic/helpers/src/date-time/dateStringToLocalTime";

const WEEK_DAYS = ["M", "T", "W", "T", "F"];
const WEEKEND_DAYS = ["S", "S"];

const UserSearchContainer = React.memo(
  ({ children }: { children: React.ReactNode }) => (
    <div className="relative">{children}</div>
  )
);

export const WeeklyTable = ({
  data,
  formattedWeek,
  authenticatedUser,
  userSearch,
  favsApi,
  hasNextPage,
  fetchNextPage,
  isFetchingNextPage,
  isLoading,
  daysCount,
  showAsMobileDaily,
  selectedDayIndex
}: {
  data: TableOverviewUser[];
  formattedWeek: string[];
  authenticatedUser: AuthenticatedUser;
  userSearch?: React.ReactNode;
  favsApi: TacticApiClient;
  hasNextPage?: boolean;
  fetchNextPage?: () => void;
  isFetchingNextPage?: boolean;
  isLoading?: boolean;
  daysCount: number;
  showAsMobileDaily?: boolean;
  selectedDayIndex?: number;
}) => {
  const { sortByDate, toggleSortOrder, filters } = useDashboardFilters();
  const { dateToSortBy, dateSortOrder, weekDate } = filters;
  const { isBreakpoint } = useBreakpoint();
  const isMd = isBreakpoint("md");
  const arrowDirection = useMemo(
    () =>
      dateSortOrder === PossibleDateSort.ASC
        ? PossibleDateSort.DESC
        : PossibleDateSort.ASC,
    [dateSortOrder]
  );
  const arrowIndex: number | undefined = useMemo(
    () => formattedWeek.indexOf(dateToSortBy ?? ""),
    [formattedWeek, dateToSortBy]
  );

  const handleDayHeaderClick = (date: string, index: number) => {
    sortByDate(date);
  };

  const currentOfficeId = authenticatedUser.user?.assigned_office_id ?? "";
  const { nameColumnWidth } = useColumnWidth();
  const offices = authenticatedUser.offices?.byId ?? {};
  // Memoize headers and limitedFormattedWeek
  const headers = React.useMemo(() => {
    const allDays = [...WEEK_DAYS, ...WEEKEND_DAYS];
    return allDays.slice(0, daysCount);
  }, [daysCount]);

  const limitedFormattedWeek = React.useMemo(() => {
    return formattedWeek
      .slice(0, daysCount)
      .filter(
        (date) =>
          !showAsMobileDaily || date === formattedWeek[selectedDayIndex || 0]
      );
  }, [formattedWeek, daysCount, selectedDayIndex, showAsMobileDaily]);

  const currentUserId = authenticatedUser.user?.id ?? "";

  const columns = React.useMemo<Column<TableOverviewUser>[]>(
    () => [
      {
        Header: "Name",
        accessor: (row: TableOverviewUser) => row.user.name,
        Control: (props: {
          column: ColumnInstance<TableOverviewUser>;
        }) => (
          <NameColumnFilter
            {...props}
            UserSearch={<UserSearchContainer>{userSearch}</UserSearchContainer>}
          />
        ),
        filter: "text",
        value: ({ row }: { row: Row<TableOverviewUser> }) =>
          row.original.user.name,
        Cell: (props: CellProps<TableOverviewUser>) => {
          return (
            <UserCell
              {...props}
              row={{
                ...props?.row,
                original: {
                  user: props.row.original.user
                }
              }}
              favsApi={favsApi}
              isMe={props.row.original.user.id === currentUserId}
              hasAnyFav={props.row.original.favorite_team_ids.length > 0}
            />
          );
        }
      },
      ...limitedFormattedWeek.map((col, index) => {
        const key = makeWeeklyTableId(headers, col, index);
        return {
          Header: headers[index],
          Control: (props: { column: ColumnInstance<TableOverviewUser> }) => (
            <SortButton column={props.column} />
          ),
          id: key,
          accessor: (row: TableOverviewUser) => {
            const status = getWorkingLocationStatus({
              assignedPolicyType: row.working_locations?.[col]?.policy?.type,
              filterOfficeId: currentOfficeId ?? null,
              officeIdOrOtherLocation: authenticatedUser.officeId ?? "",
              locationIsAssigned: !!row.working_locations?.[col]?.location,
              assignedLocation: row.working_locations?.[col]?.location ?? "",
              assignedOfficeId: row.working_locations?.[col]?.office_id ?? null,
              assignedResource:
                row.working_locations?.[col]?.schedules[0]?.resource ?? null
            });
            return status;
          },
          Cell: ({ row, value }: CellProps<TableOverviewUser>) => {
            const officeId = row.original?.working_locations?.[col]?.office_id;
            const office = officeId ? offices[officeId] : null;

            const startTime =
              row.original?.working_locations?.[col]?.schedules[0]?.start;
            const endTime =
              row.original?.working_locations?.[col]?.schedules[0]?.end;

            return (
              <div className="w-full min-w-10 md:min-w-auto">
                <OfficeStatusCell
                  officeName={office?.name}
                  officeId={office?.id}
                  status={value}
                  resource={
                    row.original?.working_locations?.[col]?.schedules[0]
                      ?.resource
                  }
                  date={col}
                  startTime={
                    startTime ? dateStringToLocalTime(startTime) : undefined
                  }
                  endTime={endTime ? dateStringToLocalTime(endTime) : undefined}
                />
              </div>
            );
          },
          sort: "text"
          // sortingFn: (rowA, rowB) => (StatusPriority[rowA.value] > StatusPriority[rowB.value])
        };
      })
    ],
    [
      data,
      limitedFormattedWeek,
      headers,
      currentOfficeId,
      favsApi,
      currentUserId,
      authenticatedUser.officeId
    ]
  );

  const tableInstance = useTable(
    {
      columns,
      data
    },
    useFilters,
    useGlobalFilter,
    useSortBy
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    tableInstance;

  const scrollRef = React.useRef<HTMLDivElement>(null);

  // Track if we're currently in any loading state
  const isCurrentlyLoading = isFetchingNextPage || isLoading;

  // Determine if we should show the InfiniteScroll spinner
  // Only show spinner during active fetch and when there's actually more data to load
  const showInfiniteScrollSpinner = isFetchingNextPage;

  // Calculate column count based on daysCount plus the name column
  const columnCount = daysCount + 1;

  // Cell styling for name column vs day columns
  const getNameCellStyle = () => ({
    width: "100%",
    height: "100%",
    margin: 0,
    display: "flex",
    alignItems: "center"
  });

  const getDayCellStyle = () => ({
    width: "100%",
    height: "100%",
    margin: "auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  });

  return (
    <TooltipProvider delayDuration={0}>
      <div className="flex flex-col">
        {!showAsMobileDaily && (
          <div className="sticky top-0 z-50 bg-background">
            <WeeklyDataGridWrapper
              nameColumnWidth={nameColumnWidth}
              daysCount={daysCount}
              className={"flex-row md:flex-col"}
            >
              {headerGroups[0].headers.map((column, columnIndex) => {
                return (
                  <div
                    {...column.getHeaderProps()}
                    key={column.id}
                    className="relative h-full w-full text-gray-700"
                    style={
                      columnIndex === 0 ? getNameCellStyle() : getDayCellStyle()
                    }
                    onClick={() => {
                      // Only handle clicks for day columns (skip name column)
                      if (columnIndex > 0) {
                        handleDayHeaderClick(
                          limitedFormattedWeek[columnIndex - 1],
                          columnIndex - 1
                        );
                      }
                    }}
                  >
                    {column.Control ? (
                      <div className="w-full">{column.render("Control")}</div>
                    ) : (
                      <div>{column.render("Header")}</div>
                    )}
                    <ArrowIcon
                      direction={
                        columnIndex - 1 === arrowIndex && arrowIndex !== -1
                          ? arrowDirection
                          : undefined
                      }
                    />
                  </div>
                );
              })}
            </WeeklyDataGridWrapper>
          </div>
        )}
        <WeeklyDataGridWrapper
          nameColumnWidth={nameColumnWidth}
          daysCount={daysCount}
        >
          <div
            {...getTableProps()}
            className="contents grid-rows-[auto]"
            ref={scrollRef}
          >
            <AuthenticatedUserRow
              authenticatedUser={authenticatedUser}
              headers={headers}
              favsApi={favsApi}
              columnCount={columnCount}
              showAsMobileDaily={showAsMobileDaily}
              selectedDayIndex={selectedDayIndex}
            />

            <div {...getTableBodyProps()} className="contents">
              {rows.map((row) => {
                prepareRow(row);

                const getCellContent = (
                  cell: (typeof row.cells)[0],
                  cellIndex: number
                ) => {
                  const { key, ...cellProps } = cell.getCellProps();
                  return (
                    <div
                      {...cellProps}
                      key={key}
                      className="overflow-hidden"
                      style={
                        cellIndex === 0 ? getNameCellStyle() : getDayCellStyle()
                      }
                    >
                      {cell.render("Cell")}
                    </div>
                  );
                };

                const userColumn = !isMd
                  ? getCellContent(row.cells[0], 0)
                  : undefined;

                const content = (
                  <>
                    {row.cells.map((cell, cellIndex) => {
                      const { key, ...cellProps } = cell.getCellProps();
                      if (!isMd && !cellIndex) return null;
                      return getCellContent(cell, cellIndex);
                    })}
                  </>
                );

                return (
                  <React.Fragment key={row.id}>
                    {isMd ? (
                      content
                    ) : (
                      <div
                        className={
                          "flex flex-shrink-0 border-b border-b-neutral-100"
                        }
                      >
                        {userColumn}
                        <div className={"flex flex-shrink-0 gap-1"}>
                          {content}
                        </div>
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
          {hasNextPage && (
            <InfiniteScroll
              hasNextPage={!!hasNextPage}
              fetchNextPage={fetchNextPage || (() => {})}
              isFetchingNextPage={!!isFetchingNextPage}
              disabled={!!isLoading || data.length === 0}
              offset={200}
              scrollRef={scrollRef}
              spinner={<WeeklyTableSkeleton columnCount={columnCount} />}
            />
          )}
        </WeeklyDataGridWrapper>
      </div>
    </TooltipProvider>
  );
};

const ArrowIcon = ({ direction }: { direction?: PossibleDateSort }) => {
  if (!direction) {
    return null;
  }
  return (
    <div className="-translate-y-1/2 absolute top-1/2 right-5">
      <ChevronUpIcon
        className={cn(
          "h-3.5 w-3.5 text-muted-foreground transition-transform duration-100",
          direction === PossibleDateSort.DESC ? "rotate-180" : ""
        )}
      />
    </div>
  );
};
